import React, { useState, useEffect } from 'react';
import { X, Plus, Trophy, DollarSign } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Prize } from '../../lib/supabase';

type Props = {
  campaignId: string;
  onClose: () => void;
};

const PrizesModal: React.FC<Props> = ({ campaignId, onClose }) => {
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [loading, setLoading] = useState(true);
  const [newPrize, setNewPrize] = useState({
    name: '',
    description: '',
    value: '',
    rank: '',
  });

  useEffect(() => {
    fetchPrizes();
  }, [campaignId]);

  const fetchPrizes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('prizes')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('rank', { ascending: true });

      if (error) throw error;
      setPrizes(data || []);
    } catch (error) {
      console.error('Error fetching prizes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('prizes')
        .insert([
          {
            campaign_id: campaignId,
            name: newPrize.name,
            description: newPrize.description || null,
            value: parseFloat(newPrize.value),
            rank: parseInt(newPrize.rank),
          },
        ]);

      if (error) throw error;

      // Reset form and refresh prizes
      setNewPrize({ name: '', description: '', value: '', rank: '' });
      fetchPrizes();
    } catch (error) {
      console.error('Error adding prize:', error);
      alert('Failed to add prize. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">Manage Prizes</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4 mb-8">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prize Name
                </label>
                <input
                  type="text"
                  value={newPrize.name}
                  onChange={(e) => setNewPrize({ ...newPrize, name: e.target.value })}
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Value ($)
                </label>
                <input
                  type="number"
                  value={newPrize.value}
                  onChange={(e) => setNewPrize({ ...newPrize, value: e.target.value })}
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <input
                type="text"
                value={newPrize.description}
                onChange={(e) => setNewPrize({ ...newPrize, description: e.target.value })}
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rank
              </label>
              <input
                type="number"
                value={newPrize.rank}
                onChange={(e) => setNewPrize({ ...newPrize, rank: e.target.value })}
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                min="1"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 transition-colors"
            >
              <Plus className="h-4 w-4 inline-block mr-2" />
              Add Prize
            </button>
          </form>

          {loading ? (
            <p className="text-center text-gray-600">Loading prizes...</p>
          ) : prizes.length === 0 ? (
            <div className="text-center text-gray-600">
              <Trophy className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No prizes added yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {prizes.map((prize) => (
                <div
                  key={prize.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <h3 className="font-semibold text-gray-800">
                      #{prize.rank} - {prize.name}
                    </h3>
                    {prize.description && (
                      <p className="text-sm text-gray-600">{prize.description}</p>
                    )}
                  </div>
                  <div className="flex items-center text-green-600">
                    <DollarSign className="h-4 w-4 mr-1" />
                    {prize.value.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrizesModal;